import type { RiskAssessmentStep } from '@/shared/hooks/api/machineryDirectiveCompliance'
import {
  RISK_AVOIDANCE_VALUES,
  RISK_FREQUENCY_VALUES,
  RISK_INJURY_VALUES,
  RISK_LIKELIHOOD_VALUES,
} from './constants/riskAssessmentEnums'

export type RiskDimensionKey =
  | 'injury'
  | 'frequency'
  | 'avoidance'
  | 'likelihood'

export const RISK_OUT_DIMENSION_LABEL_KEYS: Record<RiskDimensionKey, string> = {
  injury: 'hazardsPage.hazardDetail.riskFlow.injury',
  frequency: 'hazardsPage.hazardDetail.riskFlow.frequency',
  avoidance: 'hazardsPage.hazardDetail.riskFlow.avoidance',
  likelihood: 'hazardsPage.hazardDetail.riskFlow.likelihood',
}

export function formatRiskOutDimensionsAboveCapLabels(
  dimensions: RiskDimensionKey[],
  translateDimensionLabel: (labelKey: string) => string
): string {
  return dimensions
    .map((dimension) =>
      translateDimensionLabel(RISK_OUT_DIMENSION_LABEL_KEYS[dimension])
    )
    .join(', ')
}

export type RiskInCap = {
  injury: string | null
  frequency: string | null
  avoidance: string | null
  likelihood: string | null
}

const DIMENSION_ORDERS: Record<
  RiskDimensionKey,
  readonly { value: string; label: string }[]
> = {
  injury: RISK_INJURY_VALUES,
  frequency: RISK_FREQUENCY_VALUES,
  avoidance: RISK_AVOIDANCE_VALUES,
  likelihood: RISK_LIKELIHOOD_VALUES,
}

function getSeverityIndex(
  dimension: RiskDimensionKey,
  value: string
): number | undefined {
  const index = DIMENSION_ORDERS[dimension].findIndex(
    (item) => item.value === value
  )
  if (index < 0) {
    return undefined
  }
  return index
}

export function isRiskOutHigherThanRiskIn(
  dimension: RiskDimensionKey,
  riskOut: string,
  riskIn: string | null | undefined
): boolean {
  if (!riskIn || riskIn.trim() === '') {
    return false
  }
  const inIndex = getSeverityIndex(dimension, riskIn)
  const outIndex = getSeverityIndex(dimension, riskOut)
  if (inIndex === undefined || outIndex === undefined) {
    return false
  }
  return outIndex > inIndex
}

export function isRiskOutOptionDisabled(
  dimension: RiskDimensionKey,
  optionValue: string,
  maxAllowedValue: string | null | undefined
): boolean {
  if (!maxAllowedValue || maxAllowedValue.trim() === '') {
    return false
  }
  return isRiskOutHigherThanRiskIn(dimension, optionValue, maxAllowedValue)
}

export type RiskOutValues = {
  injury: string
  frequency: string
  avoidance: string
  likelihood: string
}

const RISK_OUT_DIMENSIONS: RiskDimensionKey[] = [
  'injury',
  'frequency',
  'avoidance',
  'likelihood',
]

export function listRiskOutDimensionsAboveCap(
  riskInCap: RiskInCap,
  riskOut: RiskOutValues
): RiskDimensionKey[] {
  const invalid: RiskDimensionKey[] = []
  for (const dimension of RISK_OUT_DIMENSIONS) {
    const value = riskOut[dimension]
    if (
      value.trim() !== '' &&
      isRiskOutHigherThanRiskIn(dimension, value, riskInCap[dimension])
    ) {
      invalid.push(dimension)
    }
  }
  return invalid
}

export function isAnyRiskOutAboveCap(
  riskInCap: RiskInCap,
  riskOut: RiskOutValues
): boolean {
  return listRiskOutDimensionsAboveCap(riskInCap, riskOut).length > 0
}

function getPreviousStepForCap(params: {
  hazardSteps: RiskAssessmentStep[]
  excludeStepId?: string
}): RiskAssessmentStep | null {
  const { hazardSteps, excludeStepId } = params

  if (excludeStepId) {
    const currentStep = hazardSteps.find((step) => step.id === excludeStepId)
    const currentSequence = currentStep?.sequence
    if (currentSequence == null) {
      return null
    }
    return (
      hazardSteps
        .filter(
          (step) => step.id !== excludeStepId && step.sequence < currentSequence
        )
        .sort((left, right) => right.sequence - left.sequence)[0] ?? null
    )
  }

  if (hazardSteps.length === 0) {
    return null
  }

  return (
    [...hazardSteps].sort((left, right) => right.sequence - left.sequence)[0] ??
    null
  )
}

export function needsHazardRiskInForCap(params: {
  hazardSteps: RiskAssessmentStep[]
  excludeStepId?: string
}): boolean {
  return getPreviousStepForCap(params) == null
}

export function isRiskInCapPending(params: {
  hazardSteps: RiskAssessmentStep[]
  excludeStepId?: string
  isStepsLoading: boolean
  isHazardLoading: boolean
}): boolean {
  if (params.isStepsLoading) {
    return true
  }
  if (!needsHazardRiskInForCap(params)) {
    return false
  }
  return params.isHazardLoading
}

export function resolveRiskInCapForStep(params: {
  hazardRiskIn: {
    injury: string | null | undefined
    frequency: string | null | undefined
    avoidance: string | null | undefined
    likelihood: string | null | undefined
  }
  hazardSteps: RiskAssessmentStep[]
  excludeStepId?: string
}): RiskInCap {
  const { hazardRiskIn, hazardSteps, excludeStepId } = params

  const previousStep = getPreviousStepForCap({ hazardSteps, excludeStepId })

  if (previousStep) {
    return {
      injury: previousStep.riskOutInjury ?? null,
      frequency: previousStep.riskOutFrequency ?? null,
      avoidance: previousStep.riskOutAvoidance ?? null,
      likelihood: previousStep.riskOutLikelihood ?? null,
    }
  }

  return {
    injury: hazardRiskIn.injury ?? null,
    frequency: hazardRiskIn.frequency ?? null,
    avoidance: hazardRiskIn.avoidance ?? null,
    likelihood: hazardRiskIn.likelihood ?? null,
  }
}

export function getRiskDimensionLabel(
  dimension: RiskDimensionKey,
  value: string
): string {
  const option = DIMENSION_ORDERS[dimension].find((item) => item.value === value)
  return option?.label ?? value
}
